import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, Shield as RolesLogoIcon } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import { Search } from 'lucide-react';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const validationSchema = Yup.object({
  name: Yup.string().required('Il nome del ruolo è obbligatorio'),
  description: Yup.string(),
});

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Errore nel caricamento dei ruoli');
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRoles = roles
    .filter((role) => {
      if (!filterName) return true;
      const search = filterName.toLowerCase();
      return (
        role.name.toLowerCase().includes(search) ||
        role.id.toString().includes(search)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'id') {
        return isAsc ? a.id - b.id : b.id - a.id;
      }
      if (orderBy === 'user_count') {
        const valA = a.user_count || 0;
        const valB = b.user_count || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      if (orderBy === 'user_percentage') {
        const valA = a.user_percentage || 0;
        const valB = b.user_percentage || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
      const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleOpen = (role = null) => {
    if (role) {
      setCurrentId(role.id);
      formik.setValues({
        name: role.name,
        description: role.description || '',
      });
    } else {
      setCurrentId(null);
      formik.resetForm();
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentId(null);
    formik.resetForm();
  };

  const openDeleteDialog = (role) => {
    setDeleteTarget(role);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/roles/${deleteTarget.id}`);
      toast.success('Ruolo eliminato con successo');
      closeDeleteDialog();
      fetchRoles();
    } catch (error) {
      console.error(error);
      toast.error('Errore nell\'eliminazione del ruolo');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        if (currentId) {
          await api.put(`/roles/${currentId}`, values);
          toast.success('Ruolo aggiornato con successo');
        } else {
          await api.post('/roles', values);
          toast.success('Ruolo creato con successo');
        }
        handleClose();
        fetchRoles();
      } catch (error) {
        console.error(error);
        toast.error('Errore nel salvataggio del ruolo');
      }
    },
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <RolesLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Gestione Ruoli
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Definizione dei ruoli applicativi e relativa distribuzione sulle risorse umane
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Nuovo Ruolo
        </Button>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca per nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'user_count'}
                    direction={orderBy === 'user_count' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_count')}
                  >
                    Totale Risorse Umane
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'user_percentage'}
                    direction={orderBy === 'user_percentage' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_percentage')}
                  >
                    % sul totale
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                    </TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          border: '1px solid rgba(245, 158, 11, 0.35)',
                          backgroundColor: 'rgba(245, 158, 11, 0.04)',
                          minWidth: 56,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#F59E0B',
                            letterSpacing: 0.2,
                          }}
                        >
                          {typeof role.user_count === 'number' ? role.user_count : 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const rawPercent = Number(role.user_percentage);
                        const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                        const clampedPercent = Math.max(0, Math.min(100, safePercent));
                        return (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={36}
                                  thickness={6}
                                  sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                />
                                <CircularProgress
                                  variant="determinate"
                                  value={clampedPercent}
                                  size={36}
                                  thickness={6}
                                  sx={{ position: 'absolute', left: 0 }}
                                  color="warning"
                                />
                                <Box
                                  sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                    {safePercent.toFixed(0)}%
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(role)} color="primary">
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton size="small" onClick={() => openDeleteDialog(role)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredRoles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun ruolo trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRoles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-role-title"
      >
        <Box sx={style}>
          <Typography id="modal-role-title" variant="h6" component="h2" mb={3}>
            {currentId ? 'Modifica Ruolo' : 'Nuovo Ruolo'}
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Nome Ruolo"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              margin="normal"
            />
            <TextField
              fullWidth
              id="description"
              name="description"
              label="Descrizione"
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              margin="normal"
            />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleClose} color="inherit">
                Annulla
              </Button>
              <Button type="submit" variant="contained">
                Salva
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile. Stai per eliminare il ruolo:
          </DialogContentText>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
